<template>
    <form @change="isFormDirty" @submit.prevent="handleSubmit" class="form-container p-4" data-cy="form-container">
        <p class="text-center text-default-red p-2" v-if="validation.message">
            {{ validation.message }}
        </p>
        <div class="form-section" data-cy="section-general" v-if="form_view == 'association'">
            <div class="form-section-header borderline mb-4">
                <div data-cy="association-settings-general-title">{{ $t('association.general.title') }}</div>
                <a-button data-cy="submit-button" class="button-submit" @click="handleSubmit">
                    {{ $t('association.general.submit') }}
                </a-button>
            </div>
            <div class="section-body">
                <div class="input-container mb-2 pr-4 md:w-2/6" data-cy="association-settings-general-association-name">
                    <ui-label>{{ $t('association.general.association_name') }}</ui-label>
                    <div class="subText">{{ association.name }}</div>
                </div>
                <div class="input-container mb-2 pr-4 md:w-2/6" data-cy="association-settings-general-association-code">
                    <ui-label>{{ $t('association.general.association_code') }}</ui-label>
                    <div class="subText">{{ association.association_code }}</div>
                </div>
                <div class="input-container mb-2 pr-4 md:w-2/6" data-cy="association-settings-general-email-address">
                    <ui-label>{{ $t('association.general.email_address') }}</ui-label>
                    <div class="subText">{{ association.email_address }}</div>
                </div>
                <div class="input-container mb-2 pr-2" data-cy="association-settings-general-logo">
                    <ui-label>{{ $t('association.general.logo') }}</ui-label>
                    <img v-if="association.association_logo" :src="association.association_logo" alt="avatar" />
                    <div v-else>
                        <div class="subText">{{ $t('association.general.no_logo') }}</div>
                    </div>
                </div>
            </div>
        </div>
        <div class="form-section" data-cy="section-general" v-if="form_view == 'admin'">
            <div class="section-body">
                <div
                    class="input-container mb-2"
                    :class="{ 'pr-4 lg:w-2/5 md:w-2/5': form_mode == 'init', 'w-1/2 pr-4': form_mode == 'drawer' }"
                    data-cy="association-settings-general-association-name"
                >
                    <ui-label>{{ $t('association.general.association_name') }}</ui-label>
                    <ui-input
                        :formatter="formatters.titleCaseFormatter"
                        v-model="v$.association.name.$model"
                        :class="{ 'form-error': v$.association.name.$error }"
                    />
                    <div
                        class="w-full mb-2"
                        data-cy="field-general-association-name-error"
                        :class="{ 'text-default-red': v$.association.name.$error }"
                        v-if="v$.association.name.required.$invalid && v$.association.name.$error"
                    >
                        {{ $t('validation.required') }}
                    </div>
                    <validation-errors :errors="validation.errors.name" v-if="validation.errors"></validation-errors>
                </div>
                <div
                    class="input-container mb-2"
                    :class="{
                        'pr-4 lg:w-2/6 md:w-2/5': form_mode == 'init',
                        'pr-4': form_mode == 'drawer',
                        'w-1/2': is_create,
                        'w-1/4': !is_create,
                    }"
                    data-cy="association-settings-general-association-code"
                >
                    <ui-label>{{ $t('association.general.association_code') }}</ui-label>
                    <ui-input
                        v-model="v$.association.association_code.$model"
                        :maxLength="10"
                        :class="{ 'form-error': v$.association.association_code.$error }"
                    />
                    <div
                        class="w-full mb-2"
                        data-cy="field-general-association-code-error"
                        :class="{ 'text-default-red': v$.association.association_code.$error }"
                        v-if="
                            v$.association.association_code.required.$invalid && v$.association.association_code.$error
                        "
                    >
                        {{ $t('validation.required') }}
                    </div>
                    <validation-errors
                        :errors="validation.errors.association_code"
                        v-if="validation.errors"
                    ></validation-errors>
                </div>
                <div
                    v-if="!is_create"
                    class="input-container mb-2"
                    :class="{ 'pr-4 lg:w-2/6 md:w-2/5': form_mode == 'init', 'w-1/4 pr-4': form_mode == 'drawer' }"
                    data-cy="association-settings-general-association-id"
                >
                    <span>
                        <ui-label>{{ $t('association.general.association_id') }}</ui-label>
                        <div class="subText pt-1">{{ association.association_id }}</div>
                    </span>
                </div>
                <div
                    class="input-container mb-2"
                    :class="{ 'pr-4 lg:w-2/6 md:w-2/5': form_mode == 'init', 'w-full pr-4': form_mode == 'drawer' }"
                    data-cy="association-settings-general-association-email"
                >
                    <ui-label>{{ $t('association.general.email_address') }}</ui-label>
                    <ui-input
                        v-model="v$.association.email_address.$model"
                        :class="{ 'form-error': v$.association.email_address.$error }"
                    />
                    <div
                        class="w-full"
                        data-cy="field-general-association-email-address-format-error"
                        :class="{ 'text-default-red': v$.association.email_address.$error }"
                        v-if="v$.association.email_address.$error && v$.association.email_address.email.$invalid"
                    >
                        {{ $t('validation.email', { attribute: 'value' }) }}
                    </div>
                    <validation-errors
                        :errors="validation.errors.email_address"
                        v-if="validation.errors"
                    ></validation-errors>
                </div>
                <div class="input-container mb-2 pr-2" data-cy="association-settings-general-logo">
                    <ui-label>
                        {{ $t('association.general.logo') }}
                        <a-tooltip placement="right" :title="$t('globals.buttons.delete')">
                            <DeleteOutlined
                                v-if="association.association_logo"
                                class="text-default-red"
                                @click="handleRemove"
                            />
                        </a-tooltip>
                    </ui-label>
                    <a-upload
                        class="w-3/4 avatar-uploader"
                        list-type="picture-card"
                        :show-upload-list="false"
                        :before-upload="beforeUpload"
                    >
                        <img v-if="association.association_logo" :src="association.association_logo" alt="avatar" />
                        <div v-else>
                            <LoadingOutlined v-if="loading" />
                            <PlusOutlined v-else />
                            <div class="ant-upload-text">{{ $t('association.general.upload') }}</div>
                        </div>
                    </a-upload>

                    <validation-errors :errors="validation.errors.association_logo" v-if="validation.errors" />
                </div>
            </div>
        </div>
        <div class="form-section" data-cy="section-email-settings" v-if="form_view == 'association'">
            <div class="form-section-header borderline">
                <div data-cy="association-settings-email-title">{{ $t('association.email.title') }}</div>
            </div>
            <div class="section-body">
                <div class="input-container mb-2 pr-4 w-full" data-cy="association-settings-email-admin-email">
                    <ui-label>{{ $t('association.email.admin_email') }}</ui-label>
                    <div class="flex flex-wrap">
                        <div class="subText" v-for="(value, index) in association.admin_email" :key="index">
                            {{ value + ',' }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="form-section" data-cy="section-email-settings" v-if="form_view == 'admin'">
            <div class="form-section-header">
                <div data-cy="association-settings-email-title">{{ $t('association.email.title') }}</div>
            </div>
            <div class="section-body">
                <div class="input-container mb-2 w-full" data-cy="association-settings-email-admin-email">
                    <ui-label>{{ $t('association.email.admin_email') }}</ui-label>
                    <ui-select
                        v-model="v$.association.admin_email.$model"
                        :class="{ 'form-error': v$.association.admin_email.$error }"
                        :mode="'tags'"
                    />
                    <div
                        class="w-full"
                        data-cy="field-email-association-email-admin-format-error"
                        :class="{ 'text-default-red': v$.association.admin_email.$error }"
                        v-if="v$.association.admin_email.$error && v$.association.admin_email.emailValidation.$invalid"
                    >
                        {{ $t('validation.emails', { attribute: 'value' }) }}
                    </div>
                    <validation-errors
                        :errors="validation.errors.admin_email"
                        v-if="validation.errors"
                    ></validation-errors>
                </div>
            </div>
        </div>
        <div class="form-section" data-cy="section-search-settings">
            <div class="form-section-header" :class="{ borderline: form_view == 'association' }">
                <div data-cy="association-settings-search-title">
                    {{ $t('association.search_settings.title') }}
                </div>
            </div>
            <div class="section-body">
                <div
                    class="input-container mb-2"
                    :class="{
                        'pr-4 lg:w-1/3 md:w-1/2': form_mode == 'init',
                        'w-1/2 pr-4': form_mode == 'drawer',
                    }"
                    data-cy="association-settings-search-name-min-length"
                >
                    <ui-label>{{ $t('association.search_settings.search_name_min_length') }}</ui-label>
                    <ui-input-number
                        @updatedValue="onFormChange"
                        :min="1"
                        :max="50"
                        v-model="v$.association.search_name_min_length.$model"
                    />
                    <p class="text-help">{{ $t('association.search_settings.help.search_name_min_length') }}</p>
                </div>
                <div
                    class="input-container mb-2"
                    :class="{
                        'pr-4 lg:w-1/3 md:w-1/2': form_mode == 'init',
                        'w-1/2 pr-4': form_mode == 'drawer',
                    }"
                    data-cy="association-settings-search-year-limit"
                >
                    <ui-label>{{ $t('association.search_settings.search_year_limit') }}</ui-label>
                    <ui-input-number
                        @updatedValue="onFormChange"
                        :min="0"
                        v-model="v$.association.search_year_limit.$model"
                    />
                    <p class="text-help">{{ $t('association.search_settings.help.search_year_limit') }}</p>
                </div>
                <div
                    class="input-container mb-2"
                    :class="{
                        'pr-4 lg:w-1/3 md:w-1/2': form_mode == 'init',
                        'w-1/2 pr-4': form_mode == 'drawer',
                    }"
                    data-cy="association-settings-search-max-limit"
                >
                    <ui-label>{{ $t('association.search_settings.search_max_limit') }}</ui-label>
                    <ui-input-number
                        @updatedValue="onFormChange"
                        :min="0"
                        :max="500"
                        v-model="v$.association.search_max_limit.$model"
                    />
                    <p class="text-help">{{ $t('association.search_settings.help.search_max_limit') }}</p>
                </div>
                <div
                    v-if="association.is_module_covervault_enabled"
                    class="input-container mb-2"
                    :class="{
                        'pr-4 lg:w-1/3 md:w-1/2': form_mode == 'init',
                        'w-1/2 pr-4': form_mode == 'drawer',
                    }"
                    data-cy="association-settings-public-cover-search-min-length"
                >
                    <ui-label>{{ $t('association.search_settings.public_cover_search_min_search_value') }}</ui-label>
                    <ui-input-number
                        @updatedValue="onFormChange"
                        :min="0"
                        v-model="v$.association.public_cover_search_min_search_value.$model"
                    />
                    <p class="text-help">
                        {{ $t('association.search_settings.help.public_cover_search_min_search_value') }}
                    </p>
                </div>
                <div
                    v-if="association.is_module_covervault_enabled"
                    class="input-container mb-2"
                    :class="{
                        'pr-4 lg:w-1/3 md:w-1/2': form_mode == 'init',
                        'w-1/2 pr-4': form_mode == 'drawer',
                    }"
                    data-cy="association-settings-public-cover-search-tagline"
                >
                    <ui-label>{{ $t('association.search_settings.public_cover_search_tagline') }}</ui-label>
                    <ui-text-area v-model="v$.association.public_cover_search_tagline.$model" />
                    <p class="text-help">{{ $t('association.search_settings.help.public_cover_search_tagline') }}</p>
                </div>
            </div>
        </div>
        <div class="form-section" data-cy="section-data-provider-settings">
            <div class="form-section-header" :class="{ borderline: form_view == 'association' }">
                <div data-cy="association-settings-data-provider-title">
                    {{ $t('association.data_provider.title') }}
                </div>
            </div>
            <div class="section-body">
                <div
                    class="input-container mb-2"
                    :class="{
                        'pr-4 lg:w-1/3 md:w-1/2': form_mode == 'init',
                        'w-1/2 pr-4': form_mode == 'drawer',
                    }"
                    data-cy="association-settings-data-provider-decline-days"
                >
                    <ui-label>{{ $t('association.data_provider.decline_days') }}</ui-label>
                    <ui-input-number
                        @updatedValue="onFormChange"
                        :min="0"
                        :max="365"
                        v-model="v$.association.decline_days.$model"
                    />
                </div>
                <div
                    class="input-container mb-2"
                    :class="{ 'pr-4 lg:w-1/3 md:w-1/2': form_mode == 'init', 'w-1/2 pr-4': form_mode == 'drawer' }"
                    data-cy="association-settings-data-provider-import-date-limit"
                >
                    <ui-label>{{ $t('association.data_provider.import_date_limit') }}</ui-label>
                    <ui-date-picker @changeDate="onFormChange" v-model="v$.association.import_date_limit.$model" />
                </div>
                <div
                    class="input-container mb-2"
                    :class="{
                        'pr-4 lg:w-1/3 md:w-1/2': form_mode == 'init',
                        'w-1/2 pr-4': form_mode == 'drawer',
                    }"
                    data-cy="association-settings-api-max-limit"
                >
                    <ui-label>{{ $t('association.data_provider.api_max_limit') }}</ui-label>
                    <ui-input-number
                        @updatedValue="onFormChange"
                        :min="1"
                        :max="500"
                        v-model="v$.association.api_max_limit.$model"
                    />
                </div>
                <div
                    class="input-container mb-2"
                    :class="{
                        'pr-4 lg:w-1/3 md:w-1/2': form_mode == 'init',
                        'w-1/2 pr-4': form_mode == 'drawer',
                    }"
                    data-cy="association-settings-api-name-min-length"
                >
                    <ui-label>{{ $t('association.data_provider.api_name_min_length') }}</ui-label>
                    <ui-input-number
                        @updatedValue="onFormChange"
                        :min="1"
                        :max="50"
                        v-model="v$.association.api_name_min_length.$model"
                    />
                </div>
                <div
                    v-if="association.is_module_claimsvault_enabled"
                    class="input-container mb-2"
                    :class="{
                        'pr-4 lg:w-1/3 md:w-1/2': form_mode == 'init',
                        'w-1/2 pr-4': form_mode == 'drawer',
                    }"
                    data-cy="association-settings-data-retention-limit-claim"
                >
                    <ui-label>{{ $t('association.data_provider.data_retention_limit_claim') }}</ui-label>
                    <ui-input-number
                        @updatedValue="onFormChange"
                        :min="0"
                        :max="100"
                        v-model="v$.association.data_retention_limit_claim.$model"
                    />
                    <p class="text-help">
                        {{ $t('association.data_provider.help.data_retention_limit_claim') }}
                    </p>
                </div>
                <div
                    v-if="association.is_module_covervault_enabled"
                    class="input-container mb-2"
                    :class="{
                        'pr-4 lg:w-1/3 md:w-1/2': form_mode == 'init',
                        'w-1/2 pr-4': form_mode == 'drawer',
                    }"
                    data-cy="association-settings-data-retention-limit-cover"
                >
                    <ui-label>{{ $t('association.data_provider.data_retention_limit_cover') }}</ui-label>
                    <ui-input-number
                        @updatedValue="onFormChange"
                        :min="0"
                        :max="100"
                        v-model="v$.association.data_retention_limit_cover.$model"
                    />
                    <p class="text-help">
                        {{ $t('association.data_provider.help.data_retention_limit_cover') }}
                    </p>
                </div>
                <div
                    v-if="association.is_module_covervault_enabled"
                    class="input-container mb-2"
                    :class="{
                        'pr-4 lg:w-1/3 md:w-1/2': form_mode == 'init',
                        'w-1/2 pr-4': form_mode == 'drawer',
                    }"
                    data-cy="association-settings-max-imediate-cover-processing-limit"
                >
                    <ui-label>{{ $t('association.data_provider.max_immediate_cover_processing_limit') }}</ui-label>
                    <ui-input-number
                        @updatedValue="onFormChange"
                        :min="0"
                        :max="100"
                        v-model="v$.association.max_immediate_cover_processing_limit.$model"
                    />
                    <p class="text-help">
                        {{ $t('association.data_provider.help.max_immediate_cover_processing_limit') }}
                    </p>
                </div>
            </div>
        </div>
        <div class="form-section" data-cy="section-user-security-settings">
            <div class="form-section-header" :class="{ borderline: form_view == 'association' }">
                <div data-cy="association-settings-user-security-title">
                    {{ $t('association.user_security.title') }}
                </div>
            </div>
            <div class="section-body">
                <div
                    class="input-container mb-2"
                    :class="{
                        'pr-4 md:w-3/12 lg:w-4/12': form_mode == 'init',
                        'w-1/2 pr-4': form_mode == 'drawer',
                    }"
                    data-cy="association-settings-user-security-password-expire-days"
                >
                    <ui-label>{{ $t('association.user_security.password_expire_days') }}</ui-label>
                    <ui-input-number
                        @updatedValue="onFormChange"
                        :min="10"
                        :max="180"
                        v-model="v$.association.password_expire_days.$model"
                        :class="{ 'form-error': v$.association.password_expire_days.$error }"
                    />
                    <p class="text-help">{{ $t('association.user_security.help.password_expire_days') }}</p>
                    <div
                        class="w-full mb-2"
                        data-cy="field-user-security-password-expire-days-error"
                        :class="{ 'text-default-red': v$.association.password_expire_days.$error }"
                        v-if="
                            v$.association.password_expire_days.required.$invalid &&
                            v$.association.password_expire_days.$error
                        "
                    >
                        {{ $t('validation.required') }}
                    </div>
                    <validation-errors
                        :errors="validation.errors.password_expire_days"
                        v-if="validation.errors"
                    ></validation-errors>
                </div>
                <div
                    class="input-container mb-2"
                    :class="{
                        'pr-4 md:w-4/12 lg:w-4/12': form_mode == 'init',
                        'w-1/2 pr-4': form_mode == 'drawer',
                    }"
                    data-cy="association-settings-user-security-user-session-timeout"
                >
                    <ui-label>{{ $t('association.user_security.user_session_timeout') }}</ui-label>
                    <ui-input :disabled="true" v-model="v$.association.user_session_timeout.$model" />
                    <p class="text-help">{{ $t('association.user_security.help.user_session_timeout') }}</p>
                </div>
                <div
                    class="input-container mb-2"
                    :class="{
                        'pr-4 md:w-3/12 lg:w-3/12': form_mode == 'init',
                        'w-1/2 pr-4': form_mode == 'drawer',
                    }"
                    data-cy="association-settings-user-security-max-login-attempts"
                >
                    <ui-label>{{ $t('association.user_security.max_login_attempts') }}</ui-label>
                    <ui-input-number
                        @updatedValue="onFormChange"
                        :min="1"
                        :max="10"
                        v-model="v$.association.max_login_attempts.$model"
                        :class="{ 'form-error': v$.association.max_login_attempts.$error }"
                    />
                    <p class="text-help">{{ $t('association.user_security.help.max_login_attempts') }}</p>
                    <div
                        class="w-full mb-2"
                        data-cy="field-user-security-max-login-attempts-error"
                        :class="{ 'text-default-red': v$.association.max_login_attempts.$error }"
                        v-if="
                            v$.association.max_login_attempts.required.$invalid &&
                            v$.association.max_login_attempts.$error
                        "
                    >
                        {{ $t('validation.required') }}
                    </div>
                    <validation-errors
                        :errors="validation.errors.max_login_attempts"
                        v-if="validation.errors"
                    ></validation-errors>
                </div>
                <div
                    class="input-container mb-2"
                    :class="{
                        'pr-4 md:w-4/12 lg:w-4/12': form_mode == 'init',
                        'w-1/2 pr-4': form_mode == 'drawer',
                    }"
                    data-cy="association-settings-user-security-lockout-time"
                >
                    <ui-label>{{ $t('association.user_security.lockout_time') }}</ui-label>
                    <ui-input-number
                        @updatedValue="onFormChange"
                        :min="30"
                        :max="720"
                        v-model="v$.association.lockout_time.$model"
                        :class="{ 'form-error': v$.association.lockout_time.$error }"
                    />
                    <p class="text-help">{{ $t('association.user_security.help.lockout_time') }}</p>
                    <div
                        class="w-full mb-2"
                        data-cy="field-user-security-lockout-time-error"
                        :class="{ 'text-default-red': v$.association.lockout_time.$error }"
                        v-if="v$.association.lockout_time.required.$invalid && v$.association.lockout_time.$error"
                    >
                        {{ $t('validation.required') }}
                    </div>
                    <validation-errors
                        :errors="validation.errors.lockout_time"
                        v-if="validation.errors"
                    ></validation-errors>
                </div>
                <div
                    class="input-container mb-2"
                    :class="{
                        'pr-4 md:w-4/12 lg:w-4/12': form_mode == 'init',
                        'w-1/2 pr-4': form_mode == 'drawer',
                    }"
                    data-cy="association-settings-user-security-password-history-count"
                >
                    <ui-label>{{ $t('association.user_security.password_history_count') }}</ui-label>
                    <ui-input-number
                        @updatedValue="onFormChange"
                        :min="1"
                        :max="20"
                        v-model="v$.association.password_history_count.$model"
                        :class="{ 'form-error': v$.association.password_history_count.$error }"
                    />
                    <p class="text-help">{{ $t('association.user_security.help.password_history_count') }}</p>
                    <div
                        class="w-full mb-2"
                        data-cy="field-user-security-password-history-count-error"
                        :class="{ 'text-default-red': v$.association.password_history_count.$error }"
                        v-if="
                            v$.association.password_history_count.required.$invalid &&
                            v$.association.password_history_count.$error
                        "
                    >
                        {{ $t('validation.required') }}
                    </div>
                    <validation-errors
                        :errors="validation.errors.password_history_count"
                        v-if="validation.errors"
                    ></validation-errors>
                </div>
                <div
                    class="input-container mb-2"
                    :class="{
                        'pr-4 md:w-3/12 lg:w-3/12': form_mode == 'init',
                        'w-1/2 pr-4': form_mode == 'drawer',
                    }"
                    data-cy="association-settings-user-security-attempt-duration"
                >
                    <ui-label>{{ $t('association.user_security.attempt_duration') }}</ui-label>
                    <ui-input-number
                        @updatedValue="onFormChange"
                        :min="5"
                        :max="60"
                        v-model="v$.association.attempt_duration.$model"
                        :class="{ 'form-error': v$.association.attempt_duration.$error }"
                    />
                    <p class="text-help">{{ $t('association.user_security.help.attempt_duration') }}</p>
                    <div
                        class="w-full mb-2"
                        data-cy="field-user-security-attempt-duration-error"
                        :class="{ 'text-default-red': v$.association.attempt_duration.$error }"
                        v-if="
                            v$.association.attempt_duration.required.$invalid && v$.association.attempt_duration.$error
                        "
                    >
                        {{ $t('validation.required') }}
                    </div>
                    <validation-errors
                        :errors="validation.errors.attempt_duration"
                        v-if="validation.errors"
                    ></validation-errors>
                </div>
                <div
                    class="input-container mb-2"
                    :class="{
                        'pr-4 md:w-4/12 lg:w-4/12': form_mode == 'init',
                        'w-1/2 pr-4': form_mode == 'drawer',
                    }"
                    data-cy="association-settings-user-security-password-minimum-length"
                >
                    <ui-label>{{ $t('association.user_security.password_minimum_length') }}</ui-label>
                    <ui-input-number
                        @updatedValue="onFormChange"
                        :min="8"
                        :max="25"
                        v-model="v$.association.password_minimum_length.$model"
                        :class="{ 'form-error': v$.association.password_minimum_length.$error }"
                    />
                    <p class="text-help">{{ $t('association.user_security.help.password_minimum_length') }}</p>
                    <div
                        class="w-full mb-2"
                        data-cy="field-user-security-password-minimum-length-error"
                        :class="{ 'text-default-red': v$.association.password_minimum_length.$error }"
                        v-if="
                            v$.association.password_minimum_length.required.$invalid &&
                            v$.association.password_minimum_length.$error
                        "
                    >
                        {{ $t('validation.required') }}
                    </div>
                    <validation-errors
                        :errors="validation.errors.password_minimum_length"
                        v-if="validation.errors"
                    ></validation-errors>
                </div>
                <div
                    class="input-container mb-2"
                    :class="{
                        'pr-4 md:w-3/12 lg:w-3/12': form_mode == 'init',
                        'w-1/2 pr-4': form_mode == 'drawer',
                    }"
                    data-cy="association-settings-user-security-password-age"
                >
                    <ui-label>{{ $t('association.user_security.password_age') }}</ui-label>
                    <ui-input-number
                        @updatedValue="onFormChange"
                        :min="1"
                        :max="10"
                        v-model="v$.association.password_age.$model"
                        :class="{ 'form-error': v$.association.password_age.$error }"
                    />
                    <p class="text-help">{{ $t('association.user_security.help.password_age') }}</p>
                    <div
                        class="w-full mb-2"
                        data-cy="field-user-security-password-age-error"
                        :class="{ 'text-default-red': v$.association.password_age.$error }"
                        v-if="v$.association.password_age.required.$invalid && v$.association.password_age.$error"
                    >
                        {{ $t('validation.required') }}
                    </div>
                    <validation-errors
                        :errors="validation.errors.password_age"
                        v-if="validation.errors"
                    ></validation-errors>
                </div>
            </div>
        </div>
        <div class="form-section mb-12" data-cy="section-flags">
            <div class="form-section-header" :class="{ borderline: form_view == 'association' }">
                <div data-cy="association-settings-user-security-flags">
                    {{ $t('association.user_security.title_flags') }}
                </div>
            </div>
            <div class="section-body">
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-1/3': form_mode == 'init', 'w-1/2 pr-4': form_mode == 'drawer' }"
                >
                    <div data-cy="association-settings-user-security-password-include-numbers">
                        <ui-checkbox v-model="association.password_include_numbers">
                            {{ $t('association.user_security.password_include_numbers') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-1/3': form_mode == 'init', 'w-1/2 pr-4': form_mode == 'drawer' }"
                >
                    <div data-cy="association-settings-user-security-password-include-symbols">
                        <ui-checkbox v-model="association.password_include_symbols">
                            {{ $t('association.user_security.password_include_symbols') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-1/4': form_mode == 'init', 'w-1/2 pr-4': form_mode == 'drawer' }"
                >
                    <div data-cy="association-settings-user-security-is-ip-address-routing">
                        <ui-checkbox v-model="association.is_ip_address_routing">
                            {{ $t('association.user_security.is_ip_address_routing') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    v-if="isEpicVaultEnabled"
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-1/3': form_mode == 'init', 'w-1/2 pr-4': form_mode == 'drawer' }"
                >
                    <div data-cy="association-settings-is-module-claimsvault-enabled">
                        <a-checkbox
                            v-model:checked="association.is_module_claimsvault_enabled"
                            @change="onClaimsVaultModuleChange"
                            :disabled="form_view == 'association'"
                        >
                            {{ $t('association.user_security.is_module_claimsvault_enabled') }}
                        </a-checkbox>
                        <div
                            class="w-full"
                            data-cy="association-settings-is-module-claimsvault-enabled-error"
                            :class="{ 'text-default-red': v$.association.is_module_claimsvault_enabled.$error }"
                            v-if="
                                v$.association.is_module_claimsvault_enabled.$error &&
                                v$.association.is_module_claimsvault_enabled.epicModules.$invalid
                            "
                        >
                            {{ $t('validation.module') }}
                        </div>
                        <validation-errors
                            :errors="validation.errors.is_module_claimsvault_enabled"
                            v-if="validation.errors"
                        ></validation-errors>
                    </div>
                </div>
                <div
                    v-if="isEpicVaultEnabled"
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-1/3': form_mode == 'init', 'w-1/2 pr-4': form_mode == 'drawer' }"
                >
                    <div data-cy="association-settings-is-module-covervault-enabled">
                        <a-checkbox
                            v-model:checked="association.is_module_covervault_enabled"
                            @change="onCoverVaultModuleChange"
                            :disabled="form_view == 'association'"
                        >
                            {{ $t('association.user_security.is_module_covervault_enabled') }}
                        </a-checkbox>
                        <div
                            class="w-full"
                            data-cy="association-settings-is-module-covervault-enabled-error"
                            :class="{ 'text-default-red': v$.association.is_module_covervault_enabled.$error }"
                            v-if="
                                v$.association.is_module_covervault_enabled.$error &&
                                v$.association.is_module_covervault_enabled.epicModules.$invalid
                            "
                        >
                            {{ $t('validation.module') }}
                        </div>
                        <validation-errors
                            :errors="validation.errors.is_module_covervault_enabled"
                            v-if="validation.errors"
                        ></validation-errors>
                    </div>
                </div>
                <div
                    v-if="isEpicVaultEnabled"
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-1/3': form_mode == 'init', 'w-1/2 pr-4': form_mode == 'drawer' }"
                >
                    <div data-cy="association-settings-enable-advanced-claims-analytics">
                        <ui-checkbox
                            v-model="association.enable_advanced_claims_analytics"
                            :disabled="form_view != 'admin'"
                        >
                            {{ $t('association.user_security.enable_advanced_claims_analytics') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    v-if="isEpicVaultEnabled"
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-1/3': form_mode == 'init', 'w-1/2 pr-4': form_mode == 'drawer' }"
                >
                    <div data-cy="association-settings-enable-advanced-covers-analytics">
                        <ui-checkbox
                            v-model="association.enable_advanced_cover_analytics"
                            :disabled="form_view != 'admin'"
                        >
                            {{ $t('association.user_security.enable_advanced_cover_analytics') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    v-if="association.is_module_cia_enabled && association.is_module_covervault_enabled"
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-1/3': form_mode == 'init', 'w-1/2 pr-4': form_mode == 'drawer' }"
                >
                    <div data-cy="association-settings-restrict-cover-data">
                        <ui-checkbox v-model="association.restrict_cover_dashboard_data" :disabled="!isSiteAdmin">
                            {{ $t('association.user_security.restrict_cover_dashboard_data') }}
                        </ui-checkbox>
                        <div
                            class="w-full"
                            data-cy="association-settings-is-module-covervault-enabled-error"
                            :class="{ 'text-default-red': v$.association.restrict_cover_dashboard_data.$error }"
                            v-if="
                                v$.association.restrict_cover_dashboard_data.$error &&
                                v$.association.restrict_cover_dashboard_data.epicModules.$invalid
                            "
                        >
                            {{ $t('validation.module') }}
                        </div>
                        <validation-errors
                            :errors="validation.errors.restrict_cover_dashboard_data"
                            v-if="validation.errors"
                        ></validation-errors>
                    </div>
                </div>
                <div
                    v-if="isEpicVaultEnabled && association.is_module_covervault_enabled"
                    class="input-container mb-2"
                    :class="{
                        'pr-4 lg:w-1/2': form_mode == 'init',
                        'w-full pr-4': form_mode == 'drawer',
                    }"
                    data-cy="association-settings-default-landing-page"
                >
                    <ui-label>{{ $t('association.user_security.default_landing_page') }}</ui-label>
                    <ui-select
                        v-model="v$.association.default_landing_page.$model"
                        :options="module_options"
                        :is-disabled="form_view == 'association'"
                    />
                </div>
                <div
                    v-if="isEpicVaultEnabled && association.is_module_claimsvault_enabled"
                    class="input-container mb-2"
                    :class="{
                        'pr-4 lg:w-1/2': form_mode == 'init',
                        'w-full pr-4': form_mode == 'drawer',
                    }"
                    data-cy="association-settings-claims-detail-layout"
                >
                    <ui-label>{{ $t('association.user_security.claims_detail_layout') }}</ui-label>
                    <ui-select
                        v-model="v$.association.claims_detail_layout.$model"
                        :options="claims_detail_layout_options"
                        :is-disabled="form_view == 'association'"
                        :class="{ 'form-error': v$.association.claims_detail_layout.$error }"
                    />
                </div>
                <div
                    v-if="isEpicVaultEnabled && association.is_module_claimsvault_enabled"
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-2/5': form_mode == 'init', 'w-1/2 pr-4': form_mode == 'drawer' }"
                >
                    <div data-cy="association-settings-is-claim-auto-removal-enabled">
                        <ui-checkbox v-model="association.is_claim_auto_removal_enabled">
                            {{ $t('association.data_provider.is_claim_auto_removal_enabled') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    v-if="isEpicVaultEnabled && association.is_module_covervault_enabled"
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-2/5': form_mode == 'init', 'w-1/2 pr-4': form_mode == 'drawer' }"
                >
                    <div data-cy="association-settings-is-cover-auto-removal-enabled">
                        <ui-checkbox v-model="association.is_cover_auto_removal_enabled">
                            {{ $t('association.data_provider.is_cover_auto_removal_enabled') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-2/5': form_mode == 'init', 'w-1/2 pr-4': form_mode == 'drawer' }"
                >
                    <div data-cy="association-settings-hide-claim-description">
                        <ui-checkbox v-model="association.is_hide_claim_description">
                            {{ $t('association.user_security.is_hide_claim_description') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    v-if="isEpicVaultEnabled && association.is_module_covervault_enabled"
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-2/5': form_mode == 'init', 'w-1/2 pr-4': form_mode == 'drawer' }"
                >
                    <div data-cy="association-settings-is-module-cia-enabled">
                        <ui-checkbox v-model="association.is_module_cia_enabled" :disabled="form_view == 'association'">
                            {{ $t('association.user_security.is_module_cia_enabled') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-2/5': form_mode == 'init', 'w-1/2 pr-4': form_mode == 'drawer' }"
                >
                    <div data-cy="association-settings-is-enable-deactivation-process">
                        <ui-checkbox v-model="association.is_enable_deactivation_process">
                            {{ $t('association.user_security.is_enable_deactivation_process') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-2/5': form_mode == 'init', 'w-1/2 pr-4': form_mode == 'drawer' }"
                >
                    <div data-cy="association-settings-is-enforce-filename-restriction">
                        <ui-checkbox
                            v-model="association.is_enforce_filename_restriction"
                            :disabled="form_view == 'association'"
                        >
                            {{ $t('association.user_security.is_enforce_filename_restriction') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-2/5': form_mode == 'init', 'w-1/2 pr-4': form_mode == 'drawer' }"
                >
                    <div data-cy="association-settings-is-allow-company-admin-assignment">
                        <ui-checkbox v-model="association.is_allow_company_admin_assignment">
                            {{ $t('association.user_security.is_allow_company_admin_assignment') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    class="input-container checkbox-container mb-2"
                    :class="{ 'lg:w-2/5': form_mode == 'init', 'w-1/2 pr-4': form_mode == 'drawer' }"
                >
                    <div data-cy="association-settings-is-tfa-enabled">
                        <ui-checkbox v-model="association.is_tfa_enabled">
                            {{ $t('association.user_security.is_tfa_enabled') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div class="input-container checkbox-container mb-2 w-full pr-4">
                    <div data-cy="association-settings-allow-dp-companies-to-add-api-users">
                        <ui-checkbox v-model="association.allow_dp_companies_to_add_api_users">
                            {{ $t('association.user_security.allow_dp_companies_to_add_api_users') }}
                        </ui-checkbox>
                    </div>
                </div>
                <div
                    class="input-container my-2"
                    :class="{
                        'pr-4 lg:w-2/5': form_mode == 'init',
                        'w-full pr-4': form_mode == 'drawer',
                    }"
                    data-cy="association-settings-days-until-next-deactivation"
                    v-if="association.is_enable_deactivation_process"
                >
                    <ui-label>{{ $t('association.user_security.days_until_next_deactivation') }}</ui-label>
                    <ui-input-number
                        @updatedValue="onFormChange"
                        :min="0"
                        :max="500"
                        v-model="v$.association.days_until_next_deactivation.$model"
                        :class="{ 'form-error': v$.association.days_until_next_deactivation.$error }"
                    />
                    <div
                        class="w-full mb-2"
                        data-cy="field-days-until-next-deactivation-error"
                        :class="{ 'text-default-red': v$.association.days_until_next_deactivation.$error }"
                        v-if="
                            v$.association.days_until_next_deactivation.required.$invalid &&
                            v$.association.days_until_next_deactivation.$error
                        "
                    >
                        {{ $t('validation.required') }}
                    </div>
                    <validation-errors
                        :errors="validation.errors.days_until_next_deactivation"
                        v-if="validation.errors"
                    ></validation-errors>
                </div>
            </div>
        </div>
        <div class="form-section mb-12" v-if="form_view == 'admin' && !is_create" data-cy="section-activation-key">
            <div class="flex items-center justify-between">
                <div class="form-section-header" :class="{ borderline: form_view == 'association' }">
                    <div data-cy="association-settings-activation-key">
                        {{ $t('association.user_security.title_activation_key') }}
                    </div>
                </div>
                <div data-cy="association-settings-expiry-date">
                    {{ $t('association.user_security.expiry_date') }}: {{ expiry_date }}
                </div>
            </div>
            <div class="input-container w-full" data-cy="association-settings-activation-key-field">
                <ui-input :disabled="true" v-model="activation_key" />
                <div class="flex w-full mt-2 justify-end">
                    <a-tooltip placement="left" :title="$t('association.user_security.generate_new_key')">
                        <a-button data-cy="update-activation-key-button" class="ml-1 px-2" @click="showUpdateModal">
                            <SyncOutlined />
                        </a-button>
                    </a-tooltip>
                    <a-tooltip placement="top" :title="$t('globals.buttons.clipboard')">
                        <a-button data-cy="copy-to-clipboard" class="ml-1 px-2" @click="copyToClipboard">
                            <SnippetsOutlined />
                        </a-button>
                    </a-tooltip>
                    <a-tooltip v-if="is_active" placement="rightTop" :title="$t('association.user_security.expire')">
                        <a-button
                            data-cy="deactivate-activation-key-button"
                            class="ml-1 px-2 general-btn-red"
                            @click="expireAssociationKey"
                        >
                            <StopOutlined />
                        </a-button>
                    </a-tooltip>
                </div>
            </div>
        </div>
        <div class="drawer-footer" v-if="form_mode == 'drawer'">
            <slot name="actions"></slot>

            <a-button data-cy="drawer-close-button" @click="onDrawerClose" class="ant-btn mr-2" :disabled="processing">
                {{ $t('admin.association_settings.view.buttons.close') }}
            </a-button>
            <ui-submit-button data-cy="drawer-save-btn" :disabled="processing">
                <span v-if="!processing">{{ $t('admin.association_settings.view.buttons.save') }}</span>
                <span v-else>{{ $t('admin.association_settings.view.buttons.saving') }}</span>
            </ui-submit-button>
        </div>
        <modal-update-activation-key
            @formSubmit="updateActivationKey"
            :processing="processing"
            ref="updateActivationKeyModal"
        />
    </form>
</template>

<script>
import UiCheckbox from '@/components/ui/Checkbox.vue';
import UiDatePicker from '@/components/ui/DatePicker.vue';
import UiInputNumber from '@/components/ui/InputNumber.vue';
import UiInput from '@/components/ui/Input.vue';
import UiLabel from '@/components/ui/Label.vue';
import UiSelect from '@/components/ui/Select.vue';
import UiSubmitButton from '@/components/ui/SubmitButton.vue';
import UiTextArea from '@/components/ui/TextArea.vue';
import ValidationErrors from '@/components/ui/ValidationErrors.vue';
import ModalUpdateActivationKey from '@/components/modals/UpdateActivationKey.vue';

import {
    DeleteOutlined,
    LoadingOutlined,
    PlusOutlined,
    SnippetsOutlined,
    StopOutlined,
    SyncOutlined,
} from '@ant-design/icons-vue';

import { useVuelidate } from '@vuelidate/core';
import { email, required } from '@vuelidate/validators';

import formatters from '@/utils/formatters.js';
import extraValidators from '@/utils/validators.js';
import User from '@/models/user.js';

export default {
    setup() {
        return { v$: useVuelidate() };
    },
    components: {
        UiCheckbox,
        UiDatePicker,
        UiInput,
        UiInputNumber,
        UiLabel,
        UiSelect,
        UiSubmitButton,
        UiTextArea,
        ModalUpdateActivationKey,
        ValidationErrors,
        DeleteOutlined,
        LoadingOutlined,
        PlusOutlined,
        SnippetsOutlined,
        StopOutlined,
        SyncOutlined,
    },
    data() {
        return {
            loading: false,
            form_dirty: false,
            processing: false,
            image_file: {},
            image_updated: false,
            activation_key: '',
            expiry_date: '',
            formatters,
            extraValidators,
            stmp_options: [
                {
                    value: 'ssl',
                    text: 'SSL',
                },
                {
                    value: 'tls',
                    text: 'TLS',
                },
                {
                    value: null,
                    text: 'None',
                },
            ],
            module_options: [
                {
                    value: 'claimsvault_dashboard',
                    text: 'ClaimsVAULT Dashboard',
                },
                {
                    value: 'claimsvault',
                    text: 'ClaimsVAULT',
                },
                {
                    value: 'covervault_dashboard',
                    text: 'CoverVAULT Dashboard',
                },
                {
                    value: 'covervault',
                    text: 'CoverVAULT',
                },
            ],
            claims_detail_layout_options: [
                {
                    value: 'default',
                    text: 'Default Layout',
                },
                {
                    value: 'orinco',
                    text: 'ORINCO Layout',
                },
            ],
            validation: {
                errors: {},
                message: '',
            },
            is_active: false,
        };
    },
    props: {
        association: Object,
        association_id: null,
        is_create: {
            type: Boolean,
            default: false,
        },
        form_mode: {
            type: String,
            default: 'drawer',
        },
        form_view: {
            type: String,
            default: 'admin',
        },
    },
    validations: {
        association: {
            name: { required },
            association_code: { required },
            email_address: { email },
            admin_email: {
                emailValidation(emails) {
                    return extraValidators.validateEmails(emails);
                },
            },
            import_date_limit: {},
            decline_days: {},
            password_expire_days: { required },
            user_session_timeout: {},
            max_login_attempts: { required },
            attempt_duration: { required },
            lockout_time: { required },
            password_history_count: { required },
            password_minimum_length: { required },
            password_age: { required },
            is_ip_address_routing: {},
            is_hide_claim_description: {},
            is_enable_deactivation_process: {},
            is_allow_company_admin_assignment: {},
            days_until_next_deactivation: { required },
            default_landing_page: {},
            password_include_numbers: {},
            password_include_symbols: {},
            api_max_limit: {},
            api_name_min_length: {},
            search_name_min_length: { required },
            search_year_limit: { required },
            search_max_limit: { required },
            is_module_cia_enabled: {
                epicModules() {
                    return this.epicModules();
                },
            },
            is_module_claimsvault_enabled: {
                epicModules() {
                    return this.epicModules();
                },
            },
            is_module_covervault_enabled: {
                epicModules() {
                    return this.epicModules();
                },
            },
            restrict_cover_dashboard_data: {},
            is_tfa_enabled: {},
            allow_dp_companies_to_add_api_users: {},
            max_immediate_cover_processing_limit: {},
            enable_advanced_claims_analytics: {},
            enable_advanced_cover_analytics: {},
            public_cover_search_tagline: {},
            public_cover_search_min_search_value: {},
            claims_detail_layout: {},
            data_retention_limit_claim: {},
            is_claim_auto_removal_enabled: {},
            data_retention_limit_cover: {},
            is_cover_auto_removal_enabled: {},
        },
    },
    mounted() {
        if (this.form_view == 'admin' && !this.is_create) {
            this.getActivationKey();
        }
    },
    computed: {
        isEpicVaultEnabled() {
            return this.$store.getters['system/isEpicvaultEnabled'];
        },
        moduleStateLink() {
            return this.$store.state.system.is_epicvault_enabled ? 'default' : 'claim';
        },
        isSiteAdmin() {
            return User.can('manage-admin');
        },
    },
    methods: {
        epicModules() {
            if (this.isEpicVaultEnabled) {
                if (!this.association.is_module_claimsvault_enabled && !this.association.is_module_covervault_enabled) {
                    return false;
                }
            }

            return true;
        },
        handleRemove(event) {
            if (this.association.association_logo != null && this.association.association_logo != '') {
                this.image_updated = true;
                this.association.association_logo = null;
            }
        },
        beforeUpload(file) {
            this.loading = true;

            this.image_file = file;
            this.image_updated = true;
            const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';

            if (!isJpgOrPng) {
                this.$notification.error({
                    message: this.$t('validation.mimes', { attribute: 'Logo', values: 'JPEG|PNG' }),
                });
            }

            const isLtKB = file.size / 1024 / 1024 < 2;
            if (!isLtKB) {
                this.$notification.error({
                    message: this.$t('validation.lt.file', { attribute: 'Logo', value: 2048 }),
                });
            }

            if (isJpgOrPng && isLtKB) {
                this.association.association_logo = URL.createObjectURL(file);
            }

            this.loading = false;

            return false;
        },
        onDrawerClose() {
            this.$emit('onDrawerClose', {});
        },
        onFormChange() {
            this.$emit('is-form-dirty', true);
        },
        isFormDirty(is_dirty) {
            this.form_dirty = is_dirty;

            this.$emit('is-form-dirty', is_dirty);
        },
        async handleSubmit() {
            if (this.processing) {
                return false;
            }

            if (!(await this.v$.$validate())) return false;

            let association = {
                ...this.association,
                import_date_limit:
                    this.association.import_date_limit !== null && this.association.import_date_limit.isValid()
                        ? this.association.import_date_limit.format('YYYY-MM-DD')
                        : null,
                admin_email: _.join(this.association.admin_email, ','),
            };

            const fm_data = new FormData();

            Object.keys(association).forEach(function (key_value, index) {
                if (association[key_value] === true) {
                    association[key_value] = 1;
                } else if (association[key_value] === false && association[key_value] !== '') {
                    association[key_value] = 0;
                }
            });

            for (var key in association) {
                if (key != 'association_logo' && association[key] != null) {
                    fm_data.append(key, association[key]);
                }
            }

            if (this.image_updated) {
                if (this.association.association_logo != null && this.association.association_logo != '') {
                    fm_data.set('association_logo', this.image_file);
                } else {
                    fm_data.set('remove_logo', true);
                }
            }

            this.image_updated = false;

            this.v$.$reset();

            fm_data.delete('user_session_timeout');

            this.processing = true;

            const id = this.association_id;
            const headers = { 'Content-Type': 'multipart/form-data' };

            if (this.is_create) {
                this.$http
                    .post('/api/associations', fm_data, { headers })
                    .then((response) => {
                        if (response.data.status == 'success') {
                            this.$notification.success({ message: response.data.message });

                            this.isFormDirty(false);

                            this.$emit('onDrawerClose', { action: 'create' });
                        } else {
                            this.$notification.error({ message: response.data.message });
                        }
                    })
                    .catch((error) => {
                        if (error.response.status == 422) {
                            this.validation = error.response.data;
                        } else if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVAA01' }),
                            });
                        }
                    })
                    .finally(() => {
                        this.processing = false;
                    });
            } else {
                let update_url =
                    this.form_view == 'admin' ? '/api/associations/' + id : '/api/associations/' + id + '/partial';

                this.$http
                    .post(update_url, fm_data, { headers })
                    .then((response) => {
                        if (response.data.status == 'success') {
                            this.$notification.success({ message: response.data.message });

                            this.isFormDirty(false);

                            this.$emit('onDrawerClose', { action: 'update' });
                        } else {
                            this.$notification.error({ message: response.data.message });
                        }
                    })
                    .catch((error) => {
                        if (error.response.status == 422) {
                            this.validation = error.response.data;
                        } else if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVAU01' }),
                            });
                        }
                    })
                    .finally(() => {
                        this.processing = false;
                    });
            }
        },
        getActivationKey() {
            this.$http
                .get('/api/associations/' + this.association_id + '/get-activation-key')
                .then((response) => {
                    this.activation_key =
                        response.data.data.activation_key == null ? '' : response.data.data.activation_key;
                    this.is_active =
                        this.$dayjs(response.data.data.expiry_date).diff(
                            this.$dayjs().startOf('day').subtract(1, 'days'),
                            'days',
                            true,
                        ) > 1;
                    this.expiry_date =
                        response.data.data.expiry_date == null
                            ? ''
                            : this.$dayjs(response.data.data.expiry_date).format('MMMM DD, YYYY');
                })
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CVAU02' }),
                    });
                });
        },
        copyToClipboard() {
            navigator.clipboard.writeText(this.activation_key);
            this.$notification.success({
                message: this.$t('globals.global.notifications.success.success-copy-to-clipboard'),
            });
        },
        showUpdateModal() {
            this.$refs.updateActivationKeyModal.showModal();
        },
        expireAssociationKey() {
            if (this.is_active) {
                let vm = this;
                this.$confirm({
                    title: this.$t('association.expire_modal.title'),
                    content: '',
                    onOk() {
                        vm.deactivateAssociation();
                    },
                    onCancel() {
                        return false;
                    },
                });
            }
        },
        updateActivationKey() {
            this.$http
                .post('/api/associations/' + this.association_id + '/update-activation-key', {
                    new_expiry_date: -this.$dayjs()
                        .startOf('day')
                        .diff(this.$refs.updateActivationKeyModal.expiry_date, 'days'),
                })
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.$notification.success({ message: response.data.message });

                        if (this.$store.state.user.association.id == this.association_id) {
                            window.location.reload();
                        } else {
                            this.getActivationKey();
                            this.$emit('reloadTable');
                        }
                    } else {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    }
                    this.$refs.updateActivationKeyModal.modal_visible = false;
                })
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CVAU03' }),
                    });
                });
        },
        deactivateAssociation() {
            this.$http
                .post('/api/associations/' + this.association_id + '/deactivate-activation-key')
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.$notification.success({ message: response.data.message });

                        if (this.$store.state.user.association.id == this.association_id) {
                            window.location.reload();
                        } else {
                            this.getActivationKey();
                            this.$emit('reloadTable');
                        }
                    } else {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    }
                })
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CVAU04' }),
                    });
                });
        },
        onClaimsVaultModuleChange(event) {
            const value = event.target.checked;

            if (!value && !this.is_create && this.form_mode == 'drawer') {
                let vm = this;

                this.$confirm({
                    title: this.$t('association.user_security.disable_claimsvault_module_modal.title'),
                    content: this.$t('association.user_security.disable_claimsvault_module_modal.content'),
                    onOk() {
                        vm.association.is_module_claimsvault_enabled = value;
                    },
                    onCancel() {
                        vm.association.is_module_claimsvault_enabled = !value;
                    },
                });
            } else {
                this.association.is_module_claimsvault_enabled = value;
            }
        },
        onCoverVaultModuleChange(event) {
            const value = event.target.checked;

            if (!value && !this.is_create && this.form_mode == 'drawer') {
                let vm = this;

                this.$confirm({
                    title: this.$t('association.user_security.disable_covervault_module_modal.title'),
                    content: this.$t('association.user_security.disable_covervault_module_modal.content'),
                    onOk() {
                        vm.association.is_module_covervault_enabled = value;
                    },
                    onCancel() {
                        vm.association.is_module_covervault_enabled = !value;
                    },
                });
            } else {
                this.association.is_module_covervault_enabled = value;
            }
        },
    },
};
</script>
